import { useEffect, useState } from "react";
import { Icon } from "../icons";
import {
  activateTwilioConnection,
  createTwilioConnection,
  listTwilioConnections,
  updateTwilioConnection,
  updateTwilioCredentials,
  type TwilioConnection,
} from "../twilioApi";
import type { AuthSession } from "../types";

type Notify = (tone: "success" | "warning" | "danger", text: string) => void;

export function TwilioWhatsAppPanel({
  organizationName,
  session,
  notify,
}: {
  organizationName: string;
  session: AuthSession;
  notify: Notify;
}) {
  const [connections, setConnections] = useState<TwilioConnection[]>([]);
  const [busy, setBusy] = useState("");
  const [editingId, setEditingId] = useState("");
  const [replacementToken, setReplacementToken] = useState("");
  const [form, setForm] = useState({
    label: `Twilio WhatsApp ${organizationName}`,
    accountSid: "",
    authToken: "",
    senderPhone: "",
    displayPhoneNumber: "",
    messagingServiceSid: "",
  });

  useEffect(() => {
    setForm({ label: `Twilio WhatsApp ${organizationName}`, accountSid: "", authToken: "", senderPhone: "", displayPhoneNumber: "", messagingServiceSid: "" });
    setEditingId("");
    setReplacementToken("");
  }, [organizationName]);

  useEffect(() => {
    let active = true;
    void listTwilioConnections(session)
      .then((items) => active && setConnections(items))
      .catch((cause) => active && notify("danger", `No se pudo cargar Twilio: ${errorMessage(cause)}`));
    return () => { active = false; };
  }, [session, notify]);

  async function refresh() {
    setConnections(await listTwilioConnections(session));
  }

  async function create() {
    setBusy("create");
    try {
      await createTwilioConnection(form, session);
      setForm((current) => ({
        ...current,
        accountSid: "",
        authToken: "",
        senderPhone: "",
        displayPhoneNumber: "",
        messagingServiceSid: "",
      }));
      await refresh();
      notify("success", "Conexión Twilio guardada. Configura sus dos webhooks y luego valida la cuenta.");
    } catch (cause) {
      notify("danger", `No se pudo guardar Twilio: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function activate(connection: TwilioConnection) {
    setBusy(`activate-${connection.connectionId}`);
    try {
      await activateTwilioConnection(connection.connectionId, session);
      await refresh();
      notify("success", "Credenciales Twilio validadas. La recepción oficial quedó activa.");
    } catch (cause) {
      await refresh().catch(() => undefined);
      notify("danger", `Twilio no pudo validar la conexión: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function toggle(connection: TwilioConnection) {
    const status = connection.status === "active" ? "paused" : "active";
    setBusy(`toggle-${connection.connectionId}`);
    try {
      await updateTwilioConnection(connection.connectionId, { status }, session);
      await refresh();
      notify("success", status === "active" ? "Twilio activado." : "Twilio pausado.");
    } catch (cause) {
      notify("danger", `No se pudo cambiar el estado de Twilio: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function replaceCredentials(connection: TwilioConnection) {
    const value = replacementToken.trim();
    if (!value) return;
    setBusy(`credentials-${connection.connectionId}`);
    try {
      await updateTwilioCredentials(connection.connectionId, value, session);
      setReplacementToken("");
      setEditingId("");
      await refresh();
      notify("success", "Auth Token reemplazado. Valida nuevamente la conexión.");
    } catch (cause) {
      notify("danger", `No se pudo reemplazar el Auth Token: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  const ready = form.label.trim() && /^AC[a-fA-F0-9]{32}$/.test(form.accountSid.trim()) &&
    form.authToken.trim().length >= 20 && form.senderPhone.replace(/\D/g, "").length >= 8;

  return (
    <section className="channel-section twilio-channel-section">
      <div className="channel-section-heading">
        <span className="connection-icon"><Icon name="whatsapp" size={20} /></span>
        <div>
          <p className="section-kicker">CANAL OFICIAL · CONVERSACIONES INDIVIDUALES</p>
          <h2>Twilio WhatsApp</h2>
          <p>Usa el número virtual para atención individual. Los grupos convencionales permanecen en el bridge Baileys con una SIM móvil separada.</p>
        </div>
      </div>

      <div className="channel-admin-grid">
        <section className="card-panel admin-panel">
          <div className="panel-title-row">
            <div><p className="section-kicker">REMITENTES</p><h2>Conexiones Twilio</h2></div>
            <span className="count-badge">{connections.length}</span>
          </div>
          <div className="connection-list">
            {connections.map((connection) => (
              <article className="connection-row" key={connection.connectionId}>
                <span className="connection-icon"><Icon name="whatsapp" size={20} /></span>
                <div>
                  <strong>{connection.label}</strong>
                  <small>{connection.displayPhoneNumber || `+${connection.senderPhone}`} · {connection.accountSidMasked}</small>
                  <small>{connection.lastInboundAt ? `Último mensaje ${formatDate(connection.lastInboundAt)}` : "Aún sin mensajes recibidos"}</small>
                </div>
                <span className={`state-chip ${connection.status}`}>{statusLabel(connection.status)}</span>
                <div className="connection-actions">
                  {connection.status !== "active" ? (
                    <button className="button primary compact" disabled={Boolean(busy)} onClick={() => void activate(connection)} type="button">Validar y activar</button>
                  ) : (
                    <button className="button soft compact" disabled={Boolean(busy)} onClick={() => void toggle(connection)} type="button">Pausar</button>
                  )}
                  <button className="button soft compact" disabled={Boolean(busy)} onClick={() => setEditingId(editingId === connection.connectionId ? "" : connection.connectionId)} type="button">
                    {editingId === connection.connectionId ? "Cerrar" : "Cambiar token"}
                  </button>
                </div>
                <div className="credential-update-panel twilio-webhook-values">
                  <label className="field-label"><span>Webhook de mensajes</span><code>{connection.webhookUrl}</code></label>
                  <button className="button soft compact" onClick={() => void copy(connection.webhookUrl, notify)} type="button">Copiar mensajes</button>
                  <label className="field-label"><span>Status Callback</span><code>{connection.statusCallbackUrl}</code></label>
                  <button className="button soft compact" onClick={() => void copy(connection.statusCallbackUrl, notify)} type="button">Copiar estados</button>
                </div>
                {connection.lastError ? <p className="connection-error">{connection.lastError}</p> : null}
                {editingId === connection.connectionId ? (
                  <div className="credential-update-panel">
                    <label className="field-label"><span>Nuevo Auth Token de Twilio</span><input type="password" autoComplete="new-password" value={replacementToken} onChange={(event) => setReplacementToken(event.target.value)} /></label>
                    <button className="button primary compact" disabled={Boolean(busy) || replacementToken.trim().length < 20} onClick={() => void replaceCredentials(connection)} type="button">Guardar token</button>
                    <p className="secret-note"><Icon name="check" size={15} /> El token se cifra en Secrets Manager y nunca vuelve a la interfaz.</p>
                  </div>
                ) : null}
              </article>
            ))}
            {!connections.length ? <div className="friendly-empty"><span><Icon name="whatsapp" size={24} /></span><strong>Twilio preparado, sin remitente configurado</strong><p>Completa el registro del WhatsApp Sender en Twilio y agrega aquí sus credenciales.</p></div> : null}
          </div>
        </section>

        <section className="card-panel admin-panel connection-form-panel">
          <div className="panel-title-row"><div><p className="section-kicker">NUEVO REMITENTE</p><h2>Conectar Twilio</h2></div></div>
          <p className="form-help">Primero registra el número como WhatsApp Sender en Twilio. Esta conexión no sirve para participar en grupos.</p>
          <div className="form-stack">
            <label className="field-label"><span>Nombre visible interno</span><input value={form.label} onChange={(event) => setForm({ ...form, label: event.target.value })} /></label>
            <label className="field-label"><span>Account SID</span><input value={form.accountSid} onChange={(event) => setForm({ ...form, accountSid: event.target.value })} placeholder="AC…" autoComplete="off" /></label>
            <label className="field-label"><span>Auth Token</span><input type="password" autoComplete="new-password" value={form.authToken} onChange={(event) => setForm({ ...form, authToken: event.target.value })} /></label>
            <label className="field-label"><span>Número WhatsApp Sender</span><input value={form.senderPhone} onChange={(event) => setForm({ ...form, senderPhone: event.target.value })} placeholder="+1… o +56…" /></label>
            <label className="field-label"><span>Número visible opcional</span><input value={form.displayPhoneNumber} onChange={(event) => setForm({ ...form, displayPhoneNumber: event.target.value })} /></label>
            <label className="field-label"><span>Messaging Service SID opcional</span><input value={form.messagingServiceSid} onChange={(event) => setForm({ ...form, messagingServiceSid: event.target.value })} placeholder="MG…" /></label>
          </div>
          <p className="secret-note"><Icon name="check" size={15} /> La firma `X-Twilio-Signature` se valida antes de aceptar cada mensaje.</p>
          <button className="button primary" disabled={Boolean(busy) || !ready} onClick={() => void create()} type="button"><Icon name="plus" size={16} /> Guardar conexión Twilio</button>
        </section>
      </div>
    </section>
  );
}

async function copy(value: string, notify: Notify) {
  try {
    await navigator.clipboard.writeText(value);
    notify("success", "Valor copiado.");
  } catch {
    notify("warning", "No fue posible copiar automáticamente.");
  }
}

function statusLabel(value: TwilioConnection["status"]) {
  return { pending: "Pendiente", active: "Activo", paused: "Pausado", error: "Con error" }[value];
}

function formatDate(value: string) {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Intl.DateTimeFormat("es-CL", { dateStyle: "short", timeStyle: "short" }).format(parsed) : value;
}

function errorMessage(cause: unknown) {
  const code = cause instanceof Error ? cause.message : "UNKNOWN_ERROR";
  const messages: Record<string, string> = {
    TWILIO_SENDER_ALREADY_CONNECTED: "ese número ya está conectado a otra organización",
    TWILIO_VALIDATION_FAILED: "revisa Account SID, Auth Token y estado del proyecto Twilio",
    TWILIO_ADMIN_REQUEST_FAILED: "AWS no pudo completar la operación",
    VALIDATION_ERROR: "revisa el formato de los datos ingresados",
  };
  return messages[code] ?? code;
}
